'use client';
import React, { Fragment, useState } from 'react';

import { Divider } from '@/app/components/divider';
import { Icon, IconType } from '@/app/components/icons';
import { Container } from '@/app/components/layouts/container';
import {
  FOOTER_DATA_GA,
  FOOTER_DATA_TEST,
} from '@/app/components/layouts/footer/tracking';
import { Link } from '@/app/components/navigation/link';
import { Accordion } from '@/app/components/text-and-data-display/accordion';
import { FOOTER_COPYRIGHT } from '@/app/contents/footer';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { stripHtmlTags } from '@/app/utils/format';

const HeadingStyled = cnva('font-noto-bold text-body-1-fixed', {
  variants: {
    link: {
      true: 'transition-color hover:text-red-500',
    },
    accordion: {
      true: 'p-0 pb-3 text-gray-900 before:border-gray-50 hover:bg-transparent focus-visible:bg-transparent',
    },
  },
  defaultVariants: {
    link: false,
    accordion: false,
  },
});

interface FooterLink {
  label?: string;
  labelHtml?: string;
  href?: string;
  target?: '_blank' | string | undefined;
}

interface FooterLinkWithIcon extends FooterLink {
  icon?: IconType;
}
export interface FooterNavItemProps {
  title?: FooterLink;
  isAccordion?: boolean;
  hasMenuHr?: boolean;
  menu?: {
    name?: string;
    links: FooterLinkWithIcon[];
  }[];
}

const FooterNavItem = ({
  title,
  isAccordion,
  hasMenuHr,
  menu = [],
}: FooterNavItemProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const renderContent = () =>
    menu.map(({ name, links }, menuIndex) => (
      <Fragment key={menuIndex}>
        {!!hasMenuHr && menuIndex > 0 && <Divider className="my-3" />}
        <div
          key={menuIndex}
          className="text-body-2-fixed space-y-2 text-gray-900 lg:space-y-1"
        >
          {!!name && <h4 className="font-noto-bold">{name}</h4>}
          {links.map(({ href, target, icon, label, labelHtml }, linkIndex) => (
            <Link
              key={linkIndex}
              href={href || ''}
              target={target}
              {...createDataAttributes({
                test: FOOTER_DATA_TEST.navLink,
              })}
              {...createDataGA({
                click: true,
                eventName: FOOTER_DATA_GA.event,
                url: href,
                text: label,
              })}
              className={cn('transition-color block hover:text-red-500', {
                'group flex gap-x-2': !!icon,
              })}
            >
              {!!icon ? (
                <>
                  <Icon
                    className="transition-color text-gray-800 group-hover:text-red-500"
                    icon={icon}
                  />
                  {labelHtml ? (
                    <span
                      className="self-center"
                      dangerouslySetInnerHTML={{ __html: labelHtml }}
                    />
                  ) : (
                    <span className="self-center">{label}</span>
                  )}
                </>
              ) : (
                <>
                  {labelHtml ? (
                    <span dangerouslySetInnerHTML={{ __html: labelHtml }} />
                  ) : (
                    label
                  )}
                </>
              )}
            </Link>
          ))}
        </div>
      </Fragment>
    ));
  if (isAccordion) {
    return (
      <Accordion
        title={title?.label}
        value={stripHtmlTags(title?.label)}
        isOpen={isOpen}
        onChange={() => setIsOpen(!isOpen)}
        content={renderContent()}
        className={HeadingStyled({ accordion: true })}
        contentClassName="px-0 space-y-3 before:border-gray-50"
      />
    );
  }

  return (
    <div className="space-y-3">
      {!!title && !!title.label && (
        <>
          {title?.href ? (
            <Link
              href={title.href}
              target={title.target}
              className={HeadingStyled({ link: true })}
              {...createDataGA({
                click: true,
                eventName: FOOTER_DATA_GA.event,
                url: title.href,
                text: title.label,
              })}
              dangerouslySetInnerHTML={{ __html: title.label }}
            />
          ) : (
            <h3
              className={HeadingStyled()}
              dangerouslySetInnerHTML={{ __html: title.label }}
            />
          )}
        </>
      )}

      {menu.length > 0 && (
        <>
          <Divider className="my-3" />
          {renderContent()}
        </>
      )}
    </div>
  );
};

export type FooterNavColumn = FooterNavItemProps[];
export interface FooterNavProps {
  items: FooterNavColumn[];
}

export const FooterNav = ({ items }: FooterNavProps) => {
  return (
    <div
      {...createDataAttributes({
        test: FOOTER_DATA_TEST.nav,
      })}
    >
      <Container>
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 py-6 lg:gap-x-6 lg:py-10">
          {items.map(
            // NOTE: group 2 columns in one parent
            (column, columnIndex) =>
              columnIndex % 2 === 0 && (
                <div
                  key={columnIndex}
                  className="max-w-80 space-y-6 lg:grid lg:max-w-none lg:grid-cols-2 lg:space-y-0 lg:gap-x-6"
                >
                  <div className="space-y-6">
                    {items[columnIndex].map((item, itemIndex) => (
                      <FooterNavItem key={itemIndex} {...item} />
                    ))}
                  </div>
                  {!!items[columnIndex + 1] && (
                    <div className="space-y-6">
                      {items[columnIndex + 1].map((item, itemIndex) => (
                        <FooterNavItem key={itemIndex} {...item} />
                      ))}
                    </div>
                  )}
                </div>
              )
          )}
        </div>
        <p className="text-caption py-6 text-gray-600 lg:text-right">
          {FOOTER_COPYRIGHT}
        </p>
      </Container>
    </div>
  );
};
